import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUILDINGS } from "@/data/content";

const linkCls = ({ isActive }) =>
  `relative text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 hover:text-lagoon ${
    isActive ? "text-lagoon" : "text-ink/70"
  }`;

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [dropOpen, setDropOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const bungalows = BUILDINGS.filter((b) => b.slug.startsWith("bungalow"));

  const go = (path) => {
    setMenuOpen(false);
    setDropOpen(false);
    navigate(path);
  };

  const goInfos = () => {
    setMenuOpen(false);
    setDropOpen(false);
    const scroll = () =>
      document.getElementById("informations")?.scrollIntoView({ behavior: "smooth" });
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 400);
    } else {
      scroll();
    }
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className="fixed inset-x-0 top-0 z-50 border-b border-ink/5 bg-white/75 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:h-20 md:px-10">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3"
            aria-label="Fafapiti Lodge — accueil"
          >
            <img src="/images/logo.png" alt="" className="h-9 w-auto md:h-11" />
            <span className="font-serif text-xl tracking-tight text-ink md:text-2xl">
              Fafapiti <span className="italic text-lagoon">Lodge</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            <NavLink to="/" end data-testid="nav-home" className={linkCls}>
              {t.nav.home}
            </NavLink>
            <NavLink to="/carte" data-testid="nav-map" className={linkCls}>
              {t.nav.map}
            </NavLink>
            <button
              data-testid="nav-infos"
              onClick={goInfos}
              className="text-[13px] font-medium uppercase tracking-[0.14em] text-ink/70 transition-colors duration-300 hover:text-lagoon"
            >
              {t.nav.infos}
            </button>
            <NavLink
              to="/batiment/maison-principale"
              data-testid="nav-main-house"
              className={linkCls}
            >
              {t.nav.mainHouse}
            </NavLink>
            <div
              className="relative"
              onMouseEnter={() => setDropOpen(true)}
              onMouseLeave={() => setDropOpen(false)}
            >
              <button
                data-testid="nav-bungalows"
                onClick={() => setDropOpen((v) => !v)}
                className="flex items-center gap-1 text-[13px] font-medium uppercase tracking-[0.14em] text-ink/70 transition-colors duration-300 hover:text-lagoon"
              >
                {t.nav.bungalows}
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`}
                />
              </button>
              <AnimatePresence>
                {dropOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="absolute left-1/2 top-full w-56 -translate-x-1/2 pt-3"
                  >
                    <div className="border border-ink/5 bg-white/95 p-2 shadow-[0_20px_50px_-20px_rgba(10,17,24,0.25)] backdrop-blur-xl">
                      {bungalows.map((b) => (
                        <button
                          key={b.slug}
                          data-testid={`nav-${b.slug}`}
                          onClick={() => go(`/batiment/${b.slug}`)}
                          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-ink/80 transition-colors duration-200 hover:bg-sand hover:text-lagoon"
                        >
                          <span>{b.name[lang]}</span>
                          <span className="font-serif text-xs italic text-ink/30">
                            {b.num}
                          </span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <NavLink to="/contact" data-testid="nav-contact" className={linkCls}>
              {t.nav.contact}
            </NavLink>
          </nav>

          <div className="flex items-center gap-4">
            <div
              data-testid="lang-switcher"
              className="flex items-center border border-ink/10 text-[12px] font-semibold uppercase tracking-widest"
            >
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-3 py-1.5 transition-colors duration-300 ${
                    lang === l
                      ? "bg-ink text-white"
                      : "text-ink/50 hover:text-ink"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <button
              data-testid="nav-menu-open"
              onClick={() => setMenuOpen(true)}
              className="text-ink lg:hidden"
              aria-label={t.nav.menu}
            >
              <Menu className="h-6 w-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-white"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <span className="flex items-center gap-3 font-serif text-xl">
                <img src="/images/logo.png" alt="" className="h-8 w-auto" />
                Fafapiti <span className="italic text-lagoon">Lodge</span>
              </span>
              <button
                data-testid="nav-menu-close"
                onClick={() => setMenuOpen(false)}
                aria-label={t.nav.close}
              >
                <X className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {[
                { label: t.nav.home, path: "/", id: "home" },
                { label: t.nav.map, path: "/carte", id: "map" },
                { label: t.nav.infos, action: goInfos, id: "infos" },
                {
                  label: t.nav.mainHouse,
                  path: "/batiment/maison-principale",
                  id: "main-house",
                },
                ...bungalows.map((b) => ({
                  label: b.name[lang],
                  path: `/batiment/${b.slug}`,
                  id: b.slug,
                })),
                { label: t.nav.contact, path: "/contact", id: "contact" },
              ].map((item, i) => (
                <motion.button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5 }}
                  onClick={() => (item.action ? item.action() : go(item.path))}
                  className="border-b border-ink/5 py-4 text-left font-serif text-3xl text-ink transition-colors duration-200 hover:text-lagoon"
                >
                  {item.label}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
